import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { UserProfile } from "@/components/feature/Sidebar";
import { SkeletonHero, SkeletonCard } from "@/components/base/Skeleton";
import RoleModuleCard from "@/components/feature/RoleModuleCard";
import ScrollReveal, { ScrollRevealContainer } from "@/components/feature/ScrollReveal";
import { ADMIN_MODULES } from "@/mocks/roleModules";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

export default function AdminPage() {
  const [user, setUser] = useState<UserProfile | null>(getStoredUser);
  const [greeting, setGreeting] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Buenos días");
    else if (hour < 19) setGreeting("Buenas tardes");
    else setGreeting("Buenas noches");
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-foreground-300 text-2xl animate-spin" />
        </span>
      </div>
    );
  }

  const today = new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      {loading ? (
        <div className="space-y-6 animate-fade-in">
          <SkeletonHero />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </div>
      ) : (
      <div className="space-y-8 animate-fade-in-up">
        {/* ── Hero ── */}
        <ScrollReveal className="scroll-reveal">
          <div className="relative overflow-hidden rounded-3xl p-6 md:p-8 bg-gradient-to-br from-foreground-700 via-foreground-800 to-foreground-900 text-white shadow-soft-lg">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-foreground-500/8 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-300 mb-1">{today}</p>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Administración del Sistema</h1>
                <p className="text-sm text-foreground-300 mt-1">{greeting}, {user.nombre.split(" ")[0]} — Herramientas disponibles</p>
              </div>
              <span className="text-xs px-3 py-1.5 bg-white/10 backdrop-blur rounded-full font-medium border border-white/15 flex items-center gap-1.5 flex-shrink-0">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                Administrador
              </span>
            </div>
          </div>
        </ScrollReveal>

        {/* ── Module Grid ── */}
        <ScrollRevealContainer
          header={<h2 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-4">Módulos disponibles para tu rol</h2>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADMIN_MODULES.map((mod, idx) => (
              <div key={mod.id} className="scroll-reveal" style={{ transitionDelay: `${idx * 60}ms` }}>
                <RoleModuleCard module={mod} index={idx} />
              </div>
            ))}
          </div>
        </ScrollRevealContainer>

        {/* ── Quick link ── */}
        <ScrollReveal className="scroll-reveal text-center">
          <button
            onClick={() => navigate("/resumen")}
            className="inline-flex items-center gap-2 px-5 py-3 bg-background-50 border border-background-200 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-100 hover:border-background-300 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-dashboard-3-line" />
            Ver panel de monitoreo
            <i className="ri-arrow-right-line" />
          </button>
        </ScrollReveal>
      </div>
      )}
    </>
  );
}